import { TFunction } from "react-i18next";
import { getPdfFontSizeOptions } from "go-core/utils/utils";
import {
	OrderPreparationStatusName,
	PdfOrientationSettingEnum,
	VenueSettingsApi,
	VenueSettingsKioskTranslationApi,
} from "../../../../../../../../../../../services/Api/Organization/types";
import { VenueSettingsItemApi } from "../../../../../../../../../../../services/Api/types";
import { getApiURL } from "../../../../../../../../../../../utils/envutil";
import {
	SettingsBasicKioskType,
	SettingsDriversType,
	SettingsKioskType,
	SettingsNumberType,
	SettingsOtherSaleType,
	SettingsOtherType,
	SettingsPaymentsAndReceiptsType,
	SettingsPreparationStatusesType,
	SettingsPrintoutType,
	SettingsReportType,
	SettingsSaleEditType,
	SettingsSaleOpenType,
	SettingsStockType,
} from "./types";

const getSettingBasedOnKey = (key: string, settings: VenueSettingsItemApi[]) => {
	return settings.find((setting) => setting.type === key);
};

const getFilteredSettings = (types: Record<string, any>, settings: VenueSettingsItemApi[]) => {
	return Object.keys(types)
		.map((key) => {
			return getSettingBasedOnKey(key, settings);
		})
		.filter((setting) => setting !== undefined) as VenueSettingsItemApi[];
};

export const getSettingsReportTypes = (settings: VenueSettingsItemApi[]) => {
	return getFilteredSettings(SettingsReportType, settings);
};

export const getSettingsSaleOpenTypes = (settings: VenueSettingsItemApi[]) => {
	return getFilteredSettings(SettingsSaleOpenType, settings);
};

export const getSettingsSaleEditTypes = (settings: VenueSettingsItemApi[]) => {
	return getFilteredSettings(SettingsSaleEditType, settings);
};

export const getSettingsOtherSaleTypes = (settings: VenueSettingsItemApi[]) => {
	return getFilteredSettings(SettingsOtherSaleType, settings);
};

export const getSettingsPaymentsAndReceiptsTypes = (settings: VenueSettingsItemApi[]) => {
	return getFilteredSettings(SettingsPaymentsAndReceiptsType, settings);
};

export const getSettingsPreparationStatusesTypes = (
	settings: VenueSettingsItemApi[],
	returnLinkConfig = false,
	t?: TFunction,
	organizationId?: number
) => {
	const settingsPreparationStatusesTypes = getFilteredSettings(SettingsPreparationStatusesType, settings);

	if (!returnLinkConfig) {
		return settingsPreparationStatusesTypes;
	}

	const kdsNumbersEnabledSettingIndex = settingsPreparationStatusesTypes.findIndex(
		(setting) => setting.type === "KDS_NUMBER_ENABLED"
	);

	if (t && kdsNumbersEnabledSettingIndex !== -1) {
		settingsPreparationStatusesTypes.splice(kdsNumbersEnabledSettingIndex, 1, {
			...settingsPreparationStatusesTypes[kdsNumbersEnabledSettingIndex],
			link: {
				to: `/public/${organizationId}/tracking`,
				description: `${t("enums.venue_settings.KDS_NUMBER_ENABLED.desc")}: `,
				name: `${getApiURL()}/public/${organizationId}/tracking`,
			},
		});
	}

	return settingsPreparationStatusesTypes;
};

export const getSettingsStockTypes = (settings: VenueSettingsItemApi[]) => {
	return getFilteredSettings(SettingsStockType, settings);
};

export const getSettingsPrintoutTypes = (settings: VenueSettingsItemApi[]) => {
	return getFilteredSettings(SettingsPrintoutType, settings);
};

export const getSettingsDriversTypes = (settings: VenueSettingsItemApi[]) => {
	return getFilteredSettings(SettingsDriversType, settings);
};

export const getSettingsOtherTypes = (settings: VenueSettingsItemApi[]) => {
	return getFilteredSettings(SettingsOtherType, settings);
};

export const getSettingsKioskTypes = (settings: VenueSettingsItemApi[]) => {
	return getFilteredSettings(SettingsKioskType, settings);
};

export const getSettingsKioskBasicTypes = (settings: VenueSettingsItemApi[]) => {
	return getFilteredSettings(SettingsBasicKioskType, settings);
};

export const getSettingsTypes = (settings: VenueSettingsItemApi[], t: TFunction, search: string) => {
	return settings.filter(
		(setting) =>
			t(`enums.venue_settings.${setting.type}.label`).toLowerCase().includes(search.toLowerCase()) ||
			t(`enums.venue_settings.${setting.type}.desc`).toLowerCase().includes(search)
	);
};

export const isSettingANumberInput = (setting: VenueSettingsItemApi) => {
	const settingsWithNumberInput = Object.keys(SettingsNumberType);
	return settingsWithNumberInput.includes(setting.type);
};

export const getSettingCustomPlaceholder = (setting: VenueSettingsItemApi, t: TFunction) => {
	switch (setting.type) {
		case SettingsDriversType.DRIVER_CAN_CHANGE_STATUS_FROM_STATUS:
			return t("common.word.lack");
		default:
			return undefined;
	}
};

export const getParsedVenueSettings = (
	settings: VenueSettingsApi,
	allSettingsOptions: VenueSettingsItemApi[],
	organizationLanguages: string[]
) => {
	const newSettings: VenueSettingsItemApi[] = [];
	allSettingsOptions.forEach((setting) => {
		const found = settings.settings?.find(({ type }) => type === setting.type);
		if (found) {
			if (!found.value) {
				found.value = "empty";
			}
			if (setting.type === "SALE_BILL_SET_STATUS_PREPARATION" && found.default_value && !found.value) {
				found.value = found.default_value;
			}

			newSettings.push(found);
		} else {
			newSettings.push({
				value: "empty",
				type: setting.type,
			});
		}
	});

	const set = settings.direction_printout;
	if (!set) {
		newSettings.push({
			value: "empty",
			type: "DIRECTION_PRINTOUT_ID",
		});
	} else {
		newSettings.push({
			value: settings.direction_printout?.id.toString(),
			type: "DIRECTION_PRINTOUT_ID",
		});
	}

	const pdfOrientationSetting = settings.settings.find((setting) => setting.type === "PDF_ORIENTATION");
	if (!pdfOrientationSetting) {
		newSettings.push({
			type: "PDF_ORIENTATION",
			default_value: "LANDSCAPE",
			value: "empty",
		});
	} else {
		newSettings.push({
			type: "PDF_ORIENTATION",
			default_value: "LANDSCAPE",
			value: pdfOrientationSetting.value,
		});
	}

	const defaultPdfFontSizeSetting = settings.settings.find((setting) => setting.type === "DEFAULT_PDF_FONT_SIZE");
	if (!defaultPdfFontSizeSetting) {
		newSettings.push({
			type: "DEFAULT_PDF_FONT_SIZE",
			default_value: "9",
			value: "empty",
		});
	} else {
		newSettings.push({
			type: "DEFAULT_PDF_FONT_SIZE",
			default_value: "9",
			value: defaultPdfFontSizeSetting.value,
		});
	}

	settings.settings = [...newSettings];

	settings.weight_barcodes = settings.weight_barcodes.map((barcode) => {
		if (typeof barcode === "string") {
			const type = getBarcodeType(barcode);
			return {
				type,
				value: barcode.replace(type, ""),
			};
		}
		return barcode;
	});

	const areStatusesIdentical = arePreparationStatusesIdentical(
		settings.supported_preparation_statuses.default_values,
		settings.supported_preparation_statuses.values
	);
	settings.preparation_statuses = areStatusesIdentical ? [] : settings.supported_preparation_statuses.values;
	settings.supported_preparation_statuses.values = areStatusesIdentical
		? []
		: settings.supported_preparation_statuses.values;

	settings.kiosk.translations = [...getParsedKioskTranslations(organizationLanguages, settings.kiosk.translations)];

	return settings;
};

export const getParsedKioskTranslations = (
	organizationLanguages: string[],
	translations: VenueSettingsKioskTranslationApi[]
) => {
	const allLanguages = [...organizationLanguages];
	const languagesFromTranslations = translations.map((translation) => translation.locale);

	languagesFromTranslations.forEach((language) => {
		if (!allLanguages.includes(language)) {
			allLanguages.push(language);
		}
	});

	allLanguages.sort((a, b) => a.localeCompare(b));

	const newKioskTranslations: VenueSettingsKioskTranslationApi[] = [];

	allLanguages.forEach((language) => {
		const translationForLanguage = translations.find((translation) => translation.locale === language);

		if (translationForLanguage) {
			newKioskTranslations.push(translationForLanguage);
		} else {
			newKioskTranslations.push({
				locale: language,
				edit_order_description: "",
				edit_order_header: "",
				start_order_description: "",
				start_order_header: "",
			});
		}
	});

	return newKioskTranslations.sort((a, b) => (a?.locale || "").localeCompare(b?.locale || ""));
};

const getBarcodeType = (barcode: string) => {
	let str = "";
	Array.from(barcode).forEach((c: string) => {
		if (c >= "0" && c <= "9") str += c;
		else return str;
	});
	return str;
};

const arePreparationStatusesIdentical = (
	defaultPreparationStatuses: OrderPreparationStatusName[],
	preparationStatuses: OrderPreparationStatusName[]
) => {
	const sortedDefaultPreparationStatuses = [...defaultPreparationStatuses].sort((a, b) => a.localeCompare(b));
	const sortedPreparationStatuses = [...preparationStatuses].sort((a, b) => a.localeCompare(b));

	return JSON.stringify(sortedDefaultPreparationStatuses) === JSON.stringify(sortedPreparationStatuses);
};

export const getOptionsByVenueSetting = (
	defaultValue: string,
	id: string,
	t: TFunction,
	supportedPreparationStatuses?: string[]
): Record<string, any>[] => {
	const saleStatusPreparationDefault = [
		{
			value: "NEW",
			label: t("enums.preparation_statuses.NEW"),
		},
		{
			value: "IN_PROGRESS",
			label: t("enums.preparation_statuses.IN_PROGRESS"),
		},
		{
			value: "READY",
			label: t("enums.preparation_statuses.READY"),
		},
		{
			value: "PACKED",
			label: t("enums.preparation_statuses.PACKED"),
		},
		{
			value: "RECEIVED",
			label: t("enums.preparation_statuses.RECEIVED"),
		},
		{
			value: "DELIVERED",
			label: t("enums.preparation_statuses.DELIVERED"),
		},
	];

	if (defaultValue === "false" || defaultValue === "true") {
		return [
			{
				value: "empty",
				label: t("common.word.default"),
			},
			{
				value: "true",
				label: t("common.word.yes", { ns: "lib" }),
			},
			{
				value: "false",
				label: t("common.word.no", { ns: "lib" }),
			},
		];
	}
	if (id === "REPORT_TYPE") {
		return [
			{
				value: "empty",
				label: t("common.word.default"),
			},
			{
				value: "ShiftWork",
				label: t("modules.venue_settings.field.shiftwork.title"),
			},
			{
				value: "Drawer",
				label: t("modules.venue_settings.field.drawer.title"),
			},
		];
	}
	if (id === "REPORT_POS_SETTLEMENT") {
		return [
			{
				value: "empty",
				label: t("common.word.default"),
			},
			{
				value: "DISABLED",
				label: t("modules.venue_settings.field.DISABLED.title"),
			},
			{
				value: "MULTI",
				label: t("modules.venue_settings.field.MULTI.title"),
			},
			{
				value: "MULTI_REQUIRED",
				label: t("modules.venue_settings.field.MULTI_REQUIRED.title"),
			},
		];
	}
	if (id === "SALE_TAX_ID_NO") {
		return [
			{
				value: "empty",
				label: t("common.word.default"),
			},
			{
				value: "DISABLED",
				label: t("modules.venue_settings.field.DISABLED.title"),
			},
			{
				value: "ENABLED",
				label: t("modules.venue_settings.field.ENABLED.title"),
			},
			{
				value: "ENABLED_ASK",
				label: t("modules.venue_settings.field.ENABLED_ASK.title"),
			},
			{
				value: "ENABLED_INVOICE_ONLY",
				label: t("modules.venue_settings.field.ENABLED_INVOICE_ONLY.title"),
			},
		];
	}
	if (id === "SALE_SAVE_ON_CLOSE") {
		return [
			{
				value: "empty",
				label: t("common.word.default"),
			},
			{
				value: "CANCEL_AND_DISMISS",
				label: t("modules.venue_settings.field.CANCEL_AND_DISMISS.title"),
			},
			{
				value: "SAVE_AND_LOGOUT",
				label: t("modules.venue_settings.field.SAVE_AND_LOGOUT.title"),
			},
			{
				value: "SAVE_AND_DISMISS",
				label: t("modules.venue_settings.field.SAVE_AND_DISMISS.title"),
			},
		];
	}
	if (id === "SALE_STATUS_PREPERATION_DEFAULT") {
		let inputValues = [...saleStatusPreparationDefault];

		if (Array.isArray(supportedPreparationStatuses) && supportedPreparationStatuses.length > 0) {
			inputValues = inputValues.filter((inputValue) => {
				return supportedPreparationStatuses.some(
					(supportedPreparationStatus) => supportedPreparationStatus === inputValue.value
				);
			});
		}

		if (
			supportedPreparationStatuses?.includes("NEW") ||
			(Array.isArray(supportedPreparationStatuses) && supportedPreparationStatuses.length === 0)
		) {
			inputValues.push({
				value: "empty",
				label: t("common.word.default"),
			});
		}

		return inputValues;
	}
	if (id === "SCANNER_TYPE") {
		return [
			{
				value: "empty",
				label: t("common.word.default"),
			},
			{
				value: "PRODUCTS",
				label: t("modules.venue_settings.field.PRODUCTS.title"),
			},
			{
				value: "CLIENTS",
				label: t("modules.venue_settings.field.CLIENTS.title"),
			},
			{
				value: "ASK",
				label: t("modules.venue_settings.field.ASK.title"),
			},
			{
				value: "AUTO",
				label: t("modules.venue_settings.field.AUTO.title"),
			},
		];
	}
	if (id === "KDS_MERGE_ITEMS") {
		return [
			{
				value: "empty",
				label: t("common.word.default"),
			},
			{
				value: "ORIGINAL",
				label: t("modules.venue_settings.field.ORIGINAL.title"),
			},
			{
				value: "MERGE",
				label: t("modules.venue_settings.field.MERGE.title"),
			},
		];
	}

	if (id === "KITCHEN_ORDER_PRINTING_TRIGGER_TYPE") {
		return [
			{
				label: t("modules.venue_settings.field.printout_templates.instant"),
				value: "INSTANT",
			},
			{
				label: t("modules.venue_settings.field.printout_templates.manual"),
				value: "MANUAL",
			},
			{
				label: t("modules.venue_settings.field.printout_templates.after_screen_lock"),
				value: "AFTER_SCREEN_LOCK",
			},
		];
	}

	if (id === "DRIVER_CAN_CHANGE_STATUS_FROM_STATUS") {
		return [
			{
				label: t("common.word.default"),
				value: "null",
			},
			...saleStatusPreparationDefault,
		];
	}

	if (id === "PDF_ORIENTATION") {
		return [
			{
				value: "empty",
				label: t("common.word.default"),
			},
			{
				label: t("enums.pdf_orientation_settings.LANDSCAPE"),
				value: PdfOrientationSettingEnum.LANDSCAPE,
			},
			{
				label: t("enums.pdf_orientation_settings.PORTRAIT"),
				value: PdfOrientationSettingEnum.PORTRAIT,
			},
			{
				label: t("enums.pdf_orientation_settings.ASK"),
				value: PdfOrientationSettingEnum.ASK,
			},
		];
	}

	if (id === "DEFAULT_PDF_FONT_SIZE") {
		return [
			{
				value: "empty",
				label: t("common.word.default"),
			},
			{
				label: t("enums.pdf_orientation_settings.ASK"),
				value: "0",
			},
			...getPdfFontSizeOptions(),
		];
	}

	if (id === "MODIFIER_GROUPS_EXPAND_TYPE") {
		return [
			{
				value: "empty",
				label: t("common.word.default"),
			},
			{
				value: "EXPAND_ONLY_ACTIVE",
				label: t("modules.venue_settings.field.EXPAND_ONLY_ACTIVE.title"),
			},
			{
				value: "EXPAND_ALL_THAT_NEEDS_ACTION",
				label: t("modules.venue_settings.field.EXPAND_ALL_THAT_NEEDS_ACTION.title"),
			},
			{
				value: "EXPAND_ALL",
				label: t("modules.venue_settings.field.EXPAND_ALL.title"),
			},
		];
	}

	return [];
};
